import type { StatusCategory } from "../api/types/StatusCategory.ts";
import type { StatusColor } from "../api/types/StatusColor.ts";

/** The palette, in picker order. Each name is a pair of shades in index.css (`--col-*`). */
export const STATUS_COLORS: StatusColor[] = ["gray", "blue", "indigo", "purple", "pink", "red", "orange", "amber", "green", "teal"];

/** How a status column wears its colour. Every class is written out so Tailwind finds it. */
export interface ColumnTone {
  /** The strip along the top of a board column. */
  strip: string;
  /** A faint wash down from the strip (a gradient `from-*`; pair with `bg-linear-to-b to-transparent`). */
  wash: string;
  /** The small dot beside the status's name. */
  dot: string;
}

const BY_COLOR: Record<StatusColor, ColumnTone> = {
  gray: { strip: "bg-col-gray", wash: "from-col-gray/15", dot: "bg-col-gray" },
  blue: { strip: "bg-col-blue", wash: "from-col-blue/12", dot: "bg-col-blue" },
  indigo: { strip: "bg-col-indigo", wash: "from-col-indigo/12", dot: "bg-col-indigo" },
  purple: { strip: "bg-col-purple", wash: "from-col-purple/12", dot: "bg-col-purple" },
  pink: { strip: "bg-col-pink", wash: "from-col-pink/12", dot: "bg-col-pink" },
  red: { strip: "bg-col-red", wash: "from-col-red/12", dot: "bg-col-red" },
  orange: { strip: "bg-col-orange", wash: "from-col-orange/12", dot: "bg-col-orange" },
  amber: { strip: "bg-col-amber", wash: "from-col-amber/15", dot: "bg-col-amber" },
  green: { strip: "bg-col-green", wash: "from-col-green/12", dot: "bg-col-green" },
  teal: { strip: "bg-col-teal", wash: "from-col-teal/12", dot: "bg-col-teal" },
};

/** No colour chosen: the category's, as before colours existed. */
const BY_CATEGORY: Record<StatusCategory, ColumnTone> = {
  not_started: { strip: "bg-border-strong", wash: "from-border-strong/15", dot: "border-[1.5px] border-muted bg-transparent" },
  active: { strip: "bg-accent", wash: "from-accent/10", dot: "bg-accent" },
  done: { strip: "bg-pass-fg", wash: "from-pass-fg/10", dot: "bg-pass-fg" },
  closed: { strip: "bg-untested", wash: "from-untested/10", dot: "border-[1.5px] border-dashed border-untested bg-transparent" },
};

export function columnTone(status: { category: StatusCategory; color?: StatusColor | null }): ColumnTone {
  return status.color ? BY_COLOR[status.color] : BY_CATEGORY[status.category];
}
